import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { IMAGES, PILLARS, SERVICES } from "../data/content";
import { Motif } from "../components/Motifs";
import { Reveal, SectionHeading } from "../components/Primitives";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMAGES.heroLoft} alt="Serene luxury loft" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#120726] via-[#120726]/85 to-[#120726]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#120726] via-transparent to-transparent" />
        </div>

        <div className="container-loft relative z-10 pt-28 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-12 bg-[#5B2C83]" />
              <p className="overline">London's Loft of Lyfe™ · Home of The 5L Method™</p>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.02] tracking-tight text-[#EFE7FA]">
              Healing Lives. <br />
              <span className="text-gradient">Illuminating Paths.</span> <br />
              Elevating Legacies.
            </h1>
            <p className="mt-8 text-lg md:text-xl font-light leading-relaxed text-[#EFE7FA]/75 max-w-xl">
              Empowering families, educators, and entrepreneurs through education, wellness,
              business strategy, and compassionate guidance.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-begin-journey" className="btn-primary">
                Begin Your Journey <ArrowRight size={18} />
              </Link>
              <Link to="/about" data-testid="hero-work-with-shantel" className="btn-ghost">
                Work With Shantel
              </Link>
              <Link to="/resources" data-testid="hero-explore-resources" className="text-[#0B7A5A] font-normal border-b border-[#0B7A5A] pb-1 hover:text-[#C4A2E8] hover:border-[#5B2C83] transition-colors">
                Explore Resources
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.img
          src={IMAGES.emblem}
          alt=""
          aria-hidden
          className="hidden lg:block absolute right-16 top-1/2 -translate-y-1/2 w-72 opacity-90 animate-float z-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 0.9, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.3 }}
        />
      </section>

      {/* BRAND STATEMENT */}
      <section className="py-20 md:py-28 bg-[#120726]">
        <div className="container-loft text-center max-w-4xl mx-auto">
          <Reveal>
            <Sparkles className="mx-auto mb-6 text-[#A8D8F0]" size={28} />
            <p className="font-serif text-2xl md:text-4xl font-light leading-snug text-[#EFE7FA]">
              A sophisticated, welcoming sanctuary where{" "}
              <span className="text-[#C4A2E8] italic">care</span>,{" "}
              <span className="text-[#0B7A5A] italic">strategy</span>, and{" "}
              <span className="text-[#C4A2E8] italic">growth</span> meet —
              so you can heal, learn, grow, and build something meaningful.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE 5L METHOD */}
      <section className="py-24 md:py-32 bg-[#1a0d33]">
        <div className="container-loft">
          <Reveal>
            <SectionHeading
              overline="The Framework"
              title="The 5L Method™"
              subtitle="Five guiding principles that weave together to help you heal, illuminate, and elevate every dimension of your life."
            />
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {PILLARS.map((p, i) => (
              <Reveal key={p.key} delay={i * 0.08}>
                <Link
                  to="/method"
                  data-testid={`home-pillar-${p.key}`}
                  className={`card-soft relative grain block p-8 md:p-10 h-full overflow-hidden ${
                    i === 3 ? "md:col-span-2" : ""
                  } ${i === 4 ? "md:col-span-1" : ""}`}
                >
                  <div className="glow-ice absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-60" />
                  <div className="relative z-10">
                    <Motif name={p.motif} className="w-12 h-12 mb-6" stroke={p.color} strokeWidth={1.1} />
                    <div className="flex items-baseline gap-3">
                      <span className="font-serif text-3xl md:text-4xl font-light" style={{ color: p.color }}>{p.name}</span>
                    </div>
                    <p className="mt-2 text-sm uppercase tracking-widest text-[#EFE7FA]/50">{p.title}</p>
                    <p className="mt-4 text-[#EFE7FA]/70 font-light leading-relaxed">{p.text}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-[#120726]">
        <div className="container-loft">
          <Reveal>
            <SectionHeading
              overline="How We Walk Together"
              title="Grow · Build · Thrive"
              subtitle="Three pathways of support, each designed to meet you exactly where you are."
            />
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {SERVICES.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.1}>
                <div data-testid={`home-service-${s.key}`} className="card-soft p-9 h-full flex flex-col">
                  <Motif name={s.motif} className="w-11 h-11 mb-6" stroke={s.color} strokeWidth={1.1} />
                  <h3 className="font-serif text-3xl font-light text-[#EFE7FA]">{s.name}</h3>
                  <p className="overline mt-2" style={{ color: s.color }}>{s.tagline}</p>
                  <p className="mt-4 text-[#EFE7FA]/70 font-light leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 space-y-2">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm text-[#EFE7FA]/75">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
                        {it}
                      </li>
                    ))}
                  </ul>
                  <Link to="/services" className="mt-7 inline-flex items-center gap-2 text-sm font-normal text-[#C4A2E8] hover:gap-3 transition-all">
                    Learn more <ArrowRight size={16} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-24 md:py-32 bg-[#1a0d33] overflow-hidden">
        <div className="container-loft grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-5 relative">
            <div className="glow-ice absolute inset-0 rounded-[2rem]" />
            <img src={IMAGES.founder} alt="Shantel London, founder" className="relative z-10 rounded-[2rem] w-full object-cover shadow-[0_30px_80px_rgba(91,44,131,0.18)]" />
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
            <p className="overline mb-4">Meet Your Guide</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight text-[#EFE7FA]">Shantel London</h2>
            <p className="mt-6 text-lg font-light leading-relaxed text-[#EFE7FA]/75">
              "I help families, educators, and entrepreneurs bloom with purpose, illuminate possibilities,
              and elevate their lives through education, wellness, business strategy, and compassionate guidance."
            </p>
            <p className="mt-4 text-[#EFE7FA]/70 font-light leading-relaxed">
              Founder of London's Loft of Lyfe™ and creator of The 5L Method™ — a holistic blueprint
              for healing, growth, and building a meaningful legacy.
            </p>
            <Link to="/about" data-testid="home-about-cta" className="btn-ghost mt-8">
              Read Shantel's Story <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative py-24 md:py-32 bg-[#231233] text-white overflow-hidden grain">
        <img src={IMAGES.lighthouse} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#231233] via-[#231233]/90 to-[#231233]/60" />
        <div className="container-loft relative z-10 text-center max-w-3xl mx-auto">
          <Reveal>
            <p className="overline text-[#A8D8F0] mb-4">Your Next Chapter</p>
            <h2 className="font-serif text-4xl md:text-6xl font-light leading-tight">
              Step into the Loft. Build something meaningful.
            </h2>
            <p className="mt-6 text-white/75 font-light text-lg">
              Whether you're here to heal, to grow, or to build a legacy — your journey begins with a single step.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="cta-band-book" className="btn-primary">Begin Your Journey</Link>
              <Link to="/services" className="btn-ghost border-white/40 text-white hover:text-[#A8D8F0] hover:border-[#A8D8F0]">Explore Services</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
